// useVendorLoadSnapshot — calendar-day load + flags for the Tracker.
//
// Returns the tabular result of `get_vendor_load_snapshot`. Has no client
// fallback: the calendar already renders without it (the `useVendorRiskSummary`
// hook owns the aggregate banner and can fall back independently).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchVendorLoadSnapshot,
  type SchedulerError,
  type SchedulerSource,
  type ServerVendorLoadDay,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseVendorLoadSnapshotOptions {
  companyId: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  enabled?: boolean;
}

export function useVendorLoadSnapshot(
  options: UseVendorLoadSnapshotOptions,
): UseSchedulerState<ServerVendorLoadDay[]> {
  const { companyId, startDate, endDate, enabled = true } = options;
  const [data, setData] = useState<ServerVendorLoadDay[] | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !companyId || !startDate || !endDate) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchVendorLoadSnapshot({ companyId, startDate, endDate });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, startDate, endDate, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
